import type { FulfillmentOrderLine } from "../types";
import type { EntryParseBaseInput, EntryParseResult } from "./confirmationEntryTypes";
import { roles, factors, unit, counts, EntryParseError, parseEntryDates, parseEntrySegments } from "./confirmationEntryPrimitives";
import { calculateFulfillmentLineAmount, validateFulfillmentLineDraft } from "./fulfillmentOrderEditorService";

export const CREW_SHORTCUTS = ["导服费", "导游电话费", "老师餐补", "司机餐补", "导游餐补", "导游路补", "导游大交通", "导游查房补", "导游住宿", "司陪住宿"];

const crewRole = (name: string) => name.match(/^(导游|导服|老师|司机|司陪)/)?.[1]?.replace("导服", "导游") || "";
const crewPeriod = (name: string) => name === "导游大交通" || /电话费/.test(name) ? "次" : /住宿/.test(name) ? "晚" : "天";

/** 仅在省略期间时读取整程；末日通常返程回家，住宿默认不计该晚。 */
function defaultBillingCount(input: EntryParseBaseInput, period: string): string {
  if (period === "次") return "1";
  const days = new Set(input.routeDays.map(day => day.dayNo)).size;
  if (!days) throw new Error(`没有可确认的行程天数，请补齐行程或明确填写实际${period === "晚" ? "晚数" : "天数"}`);
  if (period === "晚" && days === 1) throw new Error("行程为1天，默认住宿0晚，不产生住宿费用；如确有住宿，请明确填写实际晚数");
  return String(period === "晚" ? days - 1 : days);
}

/** 按原型插入角色和公式，带入真实行程期间，单价及未知人数仍由人工填写。 */
export function crewEntryShortcut(name: string, routeDays: EntryParseBaseInput["routeDays"]): string {
  const role = crewRole(name), period = crewPeriod(name);
  const days = new Set(routeDays.map(day => day.dayNo)).size;
  const duration = period === "次" ? "次数" : period === "晚" ? `${days ? days - 1 : "晚数"}晚` : `${days || "天数"}天`;
  const formula = /电话费/.test(name) ? "单价" : `${duration}×单价`;
  return `${name} ${role === "司陪" ? "人数人×" : `${role} `}${formula}`;
}

/** 新增司导归属整程；显式计费期间与原行日期分别保留。 */
function datePatch(input: EntryParseBaseInput): Partial<FulfillmentOrderLine> {
  const range = input.range || [...new Set(input.routeDays.map(day => day.dayNo))].sort((a, b) => a - b).join(",");
  if (!range) return { dayExpression: "", dayNumbers: [], serviceDates: [], serviceDateFrom: null, serviceDateTo: null, dateMappingStatus: "mapped" };
  return parseEntryDates(input.routeDays, range, "服务", "control");
}

/** 本分类服务主体资格与结算主体分别保留。 */
function providerPatch(input: EntryParseBaseInput): Partial<FulfillmentOrderLine> {
  const supplier = input.provider && "id" in input.provider ? input.provider : null;
  if (supplier && "snapshotResourceType" in supplier) {
    if (supplier.snapshotResourceType !== "导游") throw new EntryParseError(`服务主体“${supplier.name}”属于${supplier.snapshotResourceType}，不能用于导游；请重新选择本类服务主体`, "provider");
  } else if (supplier) {
    if (!supplier.canBeFulfillmentSubject) throw new EntryParseError(`服务主体“${supplier.name}”不具备当前履约资格，请重新选择可履约的主体`, "provider");
    if (supplier.type !== "guide_partner" || supplier.subjectNature !== "individual") throw new EntryParseError("导游履约主体须为有效个人导游，请在服务主体中重新选择个人导游", "provider");
  }
  return {
    fulfillmentSupplierId: supplier?.id ?? null,
    fulfillmentSupplierName: supplier?.name ?? null,
    settlementSupplierId: input.settlement?.id ?? null,
    settlementSupplierName: input.settlement?.name ?? null,
    providerAssignmentStatus: supplier ? "assigned" : "unassigned",
  };
}
/** 本分类明确资源类型和计价单位后校验完整行。 */
function basePatch(input: EntryParseBaseInput, name: string, quantity: string, count: string, price: string, quantityUnit: string, extra: Partial<FulfillmentOrderLine> = {}): Partial<FulfillmentOrderLine> {
  const amount = calculateFulfillmentLineAmount(quantity, count, price);
  const patch: Partial<FulfillmentOrderLine> = {
    resourceType: "导游", itemNameSnapshot: name, itemSpecSnapshot: null,
    quantity, billingCount: count, unitPriceAmount: price, amount, unit: quantityUnit,
    pricingUnitType: "person_day",
    reviewStatus: "confirmed", sourceType: "manual", status: "active", ...datePatch(input), ...providerPatch(input), ...extra,
  };
  const errors = validateFulfillmentLineDraft({ ...patch, id: input.allowZeroQuantity ? "existing-preview" : "local-preview" } as FulfillmentOrderLine);
  if (errors.length) throw new Error(errors.join("；"));
  return patch;
}
/** 解析本类单段原文，保留本类资源和计费约束。 */
function parseSegment(input: EntryParseBaseInput, text: string): Partial<FulfillmentOrderLine>[] {
  const raw = text.trim();
  const match = raw.match(new RegExp(`^(.+?)\\s*((?:${roles})?\\s*[\\d０-９].*)$`));
  if (!match) throw new Error("请填写费用名称和单价；省略人数、天数或晚数时使用行程默认值，也可填写实际公式");
  const name = match[1].trim().replace(/^[@＠]/, "");
  const roleMatch = match[2].trim().match(new RegExp(`^(${roles})\\s*(.*)$`));
  const inferredRole = crewRole(name);
  const role = (roleMatch?.[1] || inferredRole || "").replace("教师", "老师");
  const p = factors(roleMatch ? roleMatch[2] : match[2], undefined, input.allowZeroQuantity);
  let quantity: string;
  const defaultPeriod = crewPeriod(name);
  let count: string;
  let period = defaultPeriod;
  const quantityUnit = "人";
  if (!role) throw new Error("司导自定义项目须明确角色和实际人数");
  if (p.length === 3) { unit(p[0], ["人"]); unit(p[1], ["天", "晚", "次"]); quantity = p[0].value; count = p[1].value; period = p[1].unit || defaultPeriod; }
  else if (p.length === 2 && (p[0].unit === "人" || /电话费/.test(name))) { unit(p[0], ["人"]); quantity = p[0].value; count = defaultBillingCount(input, period); }
  else if (p.length === 2) { unit(p[0], ["天", "晚", "次"]); quantity = counts(input, role); count = p[0].value; period = p[0].unit || defaultPeriod; }
  else { quantity = counts(input, role); count = defaultBillingCount(input, period); }
  if (/电话费/.test(name) && (Number(count) !== 1 || period !== "次")) throw new Error("导游电话费只按人数×人工单价，不乘天数");

  return [basePatch(input, name === "导服费" ? "导游服务费" : name, quantity, count, p.at(-1)!.value, quantityUnit, { travelerRole: role || null, itemSpecSnapshot: `${quantityUnit}×${period}` })];
}

/** 独立解析导游文本，保留本类日期、计费和资源规则。 */
export function parseCrewEntry(input: EntryParseBaseInput): EntryParseResult {
  return parseEntrySegments(input.text, text => parseSegment(input, text), patch => [...(!patch.settlementSupplierId ? ["结算主体待设置"] : []), ...(!patch.fulfillmentSupplierId ? ["服务主体待核对"] : [])]);
}
